import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { departmentAPI, employeeAPI, positionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function slugify(text) {
  return (text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, m => m === 'đ' ? 'd' : 'D')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const CONTRACT_LABEL = {
  permanent: { label: 'Chính thức', color: '#16a34a', bg: '#dcfce7' },
  probation: { label: 'Thử việc',   color: '#d97706', bg: '#fef3c7' },
  fixed:     { label: 'Có thời hạn',color: '#0891b2', bg: '#cffafe' },
  parttime:  { label: 'Bán thời gian', color: '#7c3aed', bg: '#ede9fe' },
};

const LEVEL_ORDER = ['c_level','director','manager','lead','senior','mid','junior'];
const LEVEL_LABEL = {
  junior:    'Nhân viên',
  mid:       'Chuyên viên',
  senior:    'Chuyên viên cao cấp',
  lead:      'Trưởng nhóm',
  manager:   'Quản lý',
  director:  'Giám đốc',
  c_level:   'Ban lãnh đạo',
};
const LEVEL_COLOR = {
  c_level: '#dc2626', director: '#d97706', manager: '#7c3aed',
  lead: '#0891b2', senior: '#16a34a', mid: '#2563eb', junior: '#64748b',
};

const fmt = (n) => n ? Number(n).toLocaleString('vi-VN') : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';

function Avatar({ name, size = 36 }) {
  const initials = name ? name.split(' ').map(w => w[0]).slice(-2).join('').toUpperCase() : '?';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#1e3a8a,#0891b2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.36,
    }}>{initials}</div>
  );
}

export default function DepartmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['admin', 'hr_manager'].includes(user?.role);

  const [dept, setDept] = useState(null);
  const [allDepts, setAllDepts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('employees');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      departmentAPI.list(),
      employeeAPI.list({ department_id: id }),
      positionAPI.list(),
    ])
      .then(([dRes, eRes, pRes]) => {
        const depts = dRes.data.data;
        const found = depts.find(d => String(d.id) === String(id));
        if (!found) { setError('Không tìm thấy phòng ban'); return; }
        setDept(found);
        setAllDepts(depts);
        setEmployees(eRes.data.data || []);
        setPositions((pRes.data.data || []).filter(p => String(p.department_id) === String(id)));
      })
      .catch(() => setError('Không thể tải dữ liệu'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748b' }}>
      Đang tải...
    </div>
  );

  if (error || !dept) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>😞</div>
      <div style={{ fontSize: 15, color: '#64748b', marginBottom: 20 }}>{error || 'Không tìm thấy phòng ban'}</div>
      <button onClick={() => navigate('/departments')} style={{
        padding: '8px 20px', borderRadius: 8, border: 'none',
        background: 'linear-gradient(135deg,#1e3a8a,#0891b2)', color: '#fff', cursor: 'pointer', fontWeight: 600,
      }}>← Quay lại</button>
    </div>
  );

  const subDepts = allDepts.filter(d => String(d.parent_id) === String(id));
  const activeEmps = employees.filter(e => e.status === 'active');
  const filteredEmps = employees.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(s) ||
      (e.employee_code || '').toLowerCase().includes(s) ||
      (e.position_title || '').toLowerCase().includes(s)
    );
  });

  const tabs = [
    { key: 'employees', label: `👥 Nhân viên (${employees.length})` },
    { key: 'positions', label: `💼 Chức danh (${positions.length})` },
    ...(subDepts.length > 0 ? [{ key: 'sub', label: `📂 Phòng ban con (${subDepts.length})` }] : []),
  ];

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: '#64748b' }}>
        <button onClick={() => navigate('/departments')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#1d4ed8', fontWeight: 600, fontSize: 13, padding: 0,
        }}>← Phòng ban</button>
        {dept.parent_name && (
          <>
            <span>/</span>
            <span>{dept.parent_name}</span>
          </>
        )}
        <span>/</span>
        <span style={{ color: '#1e293b', fontWeight: 500 }}>{dept.name}</span>
      </div>

      {/* Header */}
      <div style={{
        background: '#fff', borderRadius: 14, overflow: 'hidden',
        boxShadow: '0 1px 6px rgba(0,0,0,0.08)', marginBottom: 20,
      }}>
        <div style={{ height: 5, background: 'linear-gradient(90deg,#1e3a8a,#0891b2)' }} />
        <div style={{ padding: '24px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                background: dept.parent_id
                  ? 'linear-gradient(135deg,#7c3aed,#0891b2)'
                  : 'linear-gradient(135deg,#1e3a8a,#0891b2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
              }}>
                {dept.parent_id ? '📂' : '🏬'}
              </div>
              <div>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>{dept.name}</h1>
                <div style={{ display: 'flex', gap: 12, marginTop: 5, flexWrap: 'wrap' }}>
                  {dept.parent_name && (
                    <span style={{ fontSize: 12, color: '#64748b' }}>📁 {dept.parent_name}</span>
                  )}
                  {dept.cost_center && (
                    <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#0891b2', background: '#f0f9ff', padding: '1px 8px', borderRadius: 4 }}>
                      {dept.cost_center}
                    </span>
                  )}
                  {!dept.parent_id && (
                    <span style={{ fontSize: 12, color: '#16a34a', background: '#dcfce7', padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>Phòng ban chính</span>
                  )}
                </div>
              </div>
            </div>

            {canEdit && (
              <button onClick={() => navigate('/departments')} style={{
                padding: '7px 16px', borderRadius: 8, border: '1px solid #e2e8f0',
                background: '#fff', cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600,
              }}>✏️ Quản lý</button>
            )}
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 20, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { icon: '👥', label: 'Nhân viên đang làm', value: activeEmps.length, color: '#1e3a8a' },
              { icon: '💼', label: 'Chức danh', value: positions.length, color: '#7c3aed' },
              { icon: '📂', label: 'Phòng ban con', value: subDepts.length, color: '#0891b2' },
              { icon: '🔄', label: 'Thử việc', value: employees.filter(e => e.contract_type === 'probation').length, color: '#d97706' },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', borderRadius: 10, padding: '10px 16px', flex: '1 1 160px' }}>
                <span style={{ fontSize: 20 }}>{s.icon}</span>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, borderTop: '1px solid #f1f5f9', paddingLeft: 28 }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#1d4ed8' : '#64748b',
              borderBottom: `2px solid ${tab === t.key ? '#1d4ed8' : 'transparent'}`,
              transition: 'all 0.15s',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* ── Tab: Nhân viên ── */}
      {tab === 'employees' && (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Tìm theo tên, mã NV, chức vụ..."
              style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
            />
            <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
              {filteredEmps.length}/{employees.length} nhân viên
            </span>
          </div>

          {filteredEmps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>👥</div>
              <div>{search ? 'Không tìm thấy nhân viên phù hợp' : 'Phòng ban chưa có nhân viên'}</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Nhân viên', 'Mã NV', 'Chức vụ', 'Loại HĐ', 'Ngày vào', 'Trạng thái'].map(h => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEmps.map(emp => {
                  const ct = CONTRACT_LABEL[emp.contract_type] || CONTRACT_LABEL.permanent;
                  const isActive = emp.status === 'active';
                  return (
                    <tr key={emp.id}
                      onClick={() => navigate(`/employees/${emp.id}`)}
                      style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '11px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={`${emp.first_name} ${emp.last_name}`} size={34} />
                          <div>
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{emp.last_name} {emp.first_name}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{emp.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '11px 16px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0891b2', background: '#f0f9ff', padding: '2px 8px', borderRadius: 4 }}>
                          {emp.employee_code}
                        </span>
                      </td>
                      <td style={{ padding: '11px 16px' }}>
                        {emp.position_title ? (
                          <div>
                            <div style={{ fontWeight: 500, color: '#374151' }}>{emp.position_title}</div>
                            {emp.position_level && (
                              <div style={{ fontSize: 11, color: LEVEL_COLOR[emp.position_level] || '#64748b', fontWeight: 600 }}>
                                {LEVEL_LABEL[emp.position_level] || emp.position_level}
                              </div>
                            )}
                          </div>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ padding: '11px 16px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, color: ct.color, background: ct.bg }}>
                          {ct.label}
                        </span>
                      </td>
                      <td style={{ padding: '11px 16px', color: '#64748b' }}>{fmtDate(emp.join_date)}</td>
                      <td style={{ padding: '11px 16px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                          color: isActive ? '#16a34a' : '#dc2626',
                          background: isActive ? '#dcfce7' : '#fee2e2',
                        }}>
                          {isActive ? 'Đang làm' : emp.status === 'resigned' ? 'Đã nghỉ' : 'Không hoạt động'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Tab: Chức danh ── */}
      {tab === 'positions' && (
        <div>
          {positions.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', textAlign: 'center', padding: 48, color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>💼</div>
              <div>Phòng ban chưa có chức danh nào</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
              {[...positions].sort((a, b) => LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level)).map(pos => {
                const lvlColor = LEVEL_COLOR[pos.level] || '#64748b';
                return (
                  <div key={pos.id} style={{
                    background: '#fff', borderRadius: 12, padding: '18px 20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                    borderLeft: `4px solid ${lvlColor}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{pos.title}</div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10, color: lvlColor, background: `${lvlColor}18`, flexShrink: 0, marginLeft: 8 }}>
                        {LEVEL_LABEL[pos.level] || pos.level}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>👥</span>
                        <span>{pos.employee_count || 0} nhân viên đang giữ</span>
                      </div>
                      {(pos.salary_min > 0 || pos.salary_max > 0) && (
                        <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>💰</span>
                          <span>
                            {pos.salary_min > 0 ? `${fmt(pos.salary_min)} đ` : '?'}
                            {' — '}
                            {pos.salary_max > 0 ? `${fmt(pos.salary_max)} đ` : '?'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Phòng ban con ── */}
      {tab === 'sub' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {subDepts.map(sub => (
            <div key={sub.id}
              onClick={() => navigate(`/departments/${sub.id}/${slugify(sub.name)}`)}
              style={{
                background: '#fff', borderRadius: 12, padding: '16px 20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 14, transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: 'linear-gradient(135deg,#7c3aed,#0891b2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
              }}>📂</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#1e293b' }}>{sub.name}</div>
                {sub.cost_center && (
                  <div style={{ fontSize: 11, color: '#0891b2', fontFamily: 'monospace', marginTop: 2 }}>{sub.cost_center}</div>
                )}
              </div>
              <span style={{ fontSize: 12, color: '#16a34a', background: '#dcfce7', padding: '4px 12px', borderRadius: 10, fontWeight: 600 }}>
                👤 {sub.employee_count || 0} NV
              </span>
              <span style={{ color: '#cbd5e1', fontSize: 16 }}>→</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
